import React, {Component, useEffect} from "react";

import {
	AutoComplete,
	Button,
	Col,
	Descriptions,
	Dropdown,
	Form,
	List,
	Menu as AntdMenu,
	message,
	Radio,
	Row,
	Select,
	Space,
	Spin,
	Statistic,
	Steps,
	Tag,
} from "antd";
import {useForm} from "antd/es/form/Form";

import {labels} from "../helpers/Labels";
import api from "../../api";
import {FilterRepresentation} from "../exception-reporting/ExceptionReporting";
import {
	arrayOptions,
	date_rangeOptions,
	date_range_extendedOptions,
	dateOptions,
	dateRangeFieldsFromFilterValue,
	extendedDateFieldsFromFilterValue,
	formatExtendedDateRangeValue,
	isDateRangeType,
	isExtendedDateRangeType,
	isSameFilterValue,
	numberOptions,
	stringOptions,
	toDateFilterValue,
	toDateRangeFilterValue,
	toExtendedDateRangeFilterValue,
} from "../helpers/AdvancedFIlter";
import {DataCard} from "../helpers/cards/Card";
import {
	DateRangeFilterForm,
	ExtendedDateRangeFilterForm,
	getDefaultDateRangeFields,
	getDefaultExtendedDateFields,
	validateDateRangeForm,
	validateExtendedDateRangeForm,
} from "../helpers/DateRangeFilterForm";
import {StandardForm} from "../helpers/forms/StandardForm";
import {ModalWindow} from "../helpers/modals/ModalWindow";
import dayjs from "dayjs";

const attributeComparisonOptions = {
	text: [{value: "eq", label: "Equal to"}],
	number: [
		{value: "eq", label: "Equal to"},
		{value: "gt", label: "Greater than"},
		{value: "lt", label: "Less than"},
	],
	date: [
		{value: "eq", label: "Equal to"},
		{value: "gt", label: "Greater than"},
		{value: "lt", label: "Less than"},
	],
	bool: [{value: "eq", label: "Equal to"}],
};


function PlantInfoFilter({isEdit, plant, setPlant, plantOptions, plantLoading, selectedField, onFieldChange, plantInfoFields}) {
	return (
		<DataCard
			title={isEdit ? "Edit Plant Info Filter" : "Plant Info Filters"}
			style={{marginTop: 8, borderColor: isEdit ? "#1890ff" : undefined}}>
			<Space direction="vertical" style={{width: "100%"}}>
				<div>
					<div style={{marginBottom: 4, fontWeight: 500}}>Plant (leave empty to match any plant)</div>
					<Select
						style={{width: "100%"}}
						placeholder="Select plant..."
						allowClear
						showSearch
						loading={plantLoading}
						value={plant}
						onChange={val => setPlant(val)}
						options={plantOptions}
					/>
				</div>
				<div>
					<div style={{marginBottom: 4, fontWeight: 500}}>Field</div>
					<Select
						style={{width: "100%"}}
						placeholder="Select field..."
						value={selectedField?.field || undefined}
						onChange={onFieldChange}
						options={plantInfoFields.map(f => ({label: f.label, value: f.field}))}
					/>
				</div>
			</Space>
		</DataCard>
	);
}

function MaterialFilter(props) {
	const [fields, setFields] = React.useState([]);
	const [total, setTotal] = React.useState(0);
	const [selected, setSelected] = React.useState({});
	const [proposedValues, setProposedValues] = React.useState([]);
	const [loading, setLoading] = React.useState(false);
	const [mode, setMode] = React.useState({});
	const [isEdit, setEdit] = React.useState(false);
	const [filter, setFilter] = React.useState(null);
	const [criteria, setCriteria] = React.useState({or: {}});
	const [plantEditFilter, setPlantEditFilter] = React.useState(null);
	const [leftPanel, setLeftPanel] = React.useState("attributes");

	const [plantInfoFields, setPlantInfoFields] = React.useState([]);
	const [plantPlant, setPlantPlant] = React.useState(undefined);
	const [plantPlantOptions, setPlantPlantOptions] = React.useState([]);
	const [plantPlantLoading, setPlantPlantLoading] = React.useState(false);
	const [plantSelectedField, setPlantSelectedField] = React.useState(null);
	const [plantValue, setPlantValue] = React.useState(undefined);
	const [plantValueOptions, setPlantValueOptions] = React.useState([]);
	const [plantValueLoading, setPlantValueLoading] = React.useState(false);
	const [plantMode, setPlantMode] = React.useState("eq");
	const [compareWith, setCompareWith] = React.useState("value");
	const [comparedField, setComparedField] = React.useState(null);

	const isPlantEdit = plantEditFilter !== null && plantEditFilter !== undefined;

	const attributeLabels = React.useMemo(() => {
		const mapping = {};
		for (const attr of fields) {
			mapping[attr.field] = attr.label;
		}
		return mapping;
	}, [fields]);

	const comparableFields = React.useMemo(
		() => [
			...fields.map(field => ({
				...field,
				id: `attribute:${field.field}`,
				key: field.field,
				group: "Attributes",
			})),
			...plantInfoFields.map(field => ({
				...field,
				id: `plant_info:${field.field}`,
				key: `plant_info.${field.field}`,
				group: "Plant Info",
			})),
		],
		[fields, plantInfoFields],
	);

	const getComparisonFieldOptions = (type, leftKey) =>
		["Attributes", "Plant Info"]
			.map(group => ({
				label: group,
				options: comparableFields
					.filter(field => field.group === group && field.type === type && field.key !== leftKey)
					.map(field => ({label: field.label, value: field.id})),
			}))
			.filter(group => group.options.length > 0);

	const onComparedFieldChange = id => {
		setComparedField(comparableFields.find(field => field.id === id) || null);
	};

	const getComparisonValue = () => ({
		key: comparedField.key,
		label: comparedField.label,
		type: comparedField.type,
	});

	const getFields = () => {
		setLoading(true);
		api.get(`/projects/${props.projectId}/materials/attributes`)
			.then(json => setFields(json.data))
			.finally(() => setLoading(false));
	};

	const getOptionsByType = () => {
		if (compareWith === "attribute") {
			return attributeComparisonOptions[selected.type] || [];
		}
		if (selected.type === "text") {
			return stringOptions;
		} else if (selected.type === "number") {
			return numberOptions;
		} else if (selected.type === "array") {
			return arrayOptions;
		} else if (selected.type === "date") {
			return dateOptions;
		} else if (selected.type === "date_range") {
			return date_rangeOptions;
		} else if (isExtendedDateRangeType(selected.type)) {
			return date_range_extendedOptions;
		}
		return [];
	};

	const getProposedValues = () => {
		if (isDateRangeType(selected.type) || selected.field === "long_description") {
			return;
		}
		setLoading(true);
		api.get(`/projects/${props.projectId}/materials/attributes/values?attr=${selected.field}`)
			.then(json => {
				setProposedValues(json.data);
			})
			.finally(() => setLoading(false));
	};

	useEffect(() => {
		getFields();
	}, [props.projectId]);

	useEffect(() => {}, [props.criteria]);

	useEffect(() => {
		api.get(`/projects/${props.projectId}/materials/plant-info/fields`)
			.then(json => setPlantInfoFields(json.data || []));
		setPlantPlantLoading(true);
		api.get(`/projects/${props.projectId}/materials/plant-info/values?field=plant`)
			.then(json => setPlantPlantOptions((json.data || []).map(v => ({label: v, value: v}))))
			.finally(() => setPlantPlantLoading(false));
	}, [props.projectId]);

	useEffect(() => {
		if (!plantEditFilter) return;
		const field = plantInfoFields.find(f => f.field === plantEditFilter.field) || null;
		setPlantSelectedField(field);
		setPlantPlant(plantEditFilter.plant || undefined);
		setPlantMode(plantEditFilter.mode);
		if (plantEditFilter.compare_with === "attribute") {
			setCompareWith("attribute");
			setComparedField(comparableFields.find(f => f.key === plantEditFilter.value?.key) || null);
			return;
		}
		setCompareWith("value");
		setComparedField(null);
		if (isExtendedDateRangeType(field?.type)) {
			setPlantValue(plantEditFilter.value);
			plantValueForm.setFieldsValue(getExtendedPlantInitialValues(plantEditFilter.mode));
		} else if (field) {
			setPlantValueLoading(true);
			api.get(`/projects/${props.projectId}/materials/plant-info/values?field=${encodeURIComponent(field.field)}`)
				.then(json => {
					setPlantValueOptions((json.data || []).map(v => ({label: v, value: v})));
					setPlantValue(
						plantEditFilter.mode === "in"
							? Array.isArray(plantEditFilter.value)
								? plantEditFilter.value
								: [plantEditFilter.value]
							: String(plantEditFilter.value),
					);
				})
				.finally(() => setPlantValueLoading(false));
		}
	}, [plantEditFilter]);

	useEffect(() => {
		if (!selected.field) {
			return;
		}
		if (filter?.compare_with === "attribute" && filter.key === selected.field) {
			setCompareWith("attribute");
			setComparedField(comparableFields.find(f => f.key === filter.value?.key) || null);
			setMode(filter.mode);
			return;
		}
		if (selected.type === "date_range") {
			const rangeFields =
				isEdit && filter?.type === "date_range" && filter.key === selected.field
					? dateRangeFieldsFromFilterValue(filter.value)
					: getDefaultDateRangeFields();
			attrValueForm.setFieldsValue(rangeFields);
			setMode("between");
			return;
		}
		if (isExtendedDateRangeType(selected.type)) {
			const nextMode =
				isEdit && isExtendedDateRangeType(filter?.type) && filter.key === selected.field
					? filter.mode
					: "between";
			attrValueForm.setFieldsValue(getExtendedAttributeInitialValues(nextMode));
			setMode(nextMode);
			return;
		}
		if (filter && selected.field === filter.key) {
			getProposedValues();
		} else {
			getProposedValues();
			setMode(getDefaultMode());
		}
	}, [selected]);

	useEffect(() => {
		setSelected({});
		setCompareWith("value");
		setComparedField(null);
		attrValueForm.resetFields(["name", "value"]);
	}, [props.filters]);

	const [attrValueForm] = useForm();
	const [plantValueForm] = useForm();

	const getExtendedAttributeInitialValues = nextMode => {
		if (isEdit && isExtendedDateRangeType(filter?.type) && filter.key === selected.field && filter.mode === nextMode) {
			return extendedDateFieldsFromFilterValue(filter.value, nextMode);
		}
		return getDefaultExtendedDateFields(nextMode);
	};

	const resetExtendedAttributeForm = nextMode => {
		attrValueForm.resetFields(["value", "valueFrom", "valueTo"]);
		attrValueForm.setFieldsValue(getDefaultExtendedDateFields(nextMode));
	};

	const getExtendedPlantInitialValues = nextMode => {
		if (
			isPlantEdit &&
			isExtendedDateRangeType(plantEditFilter?.field_type) &&
			plantEditFilter.mode === nextMode
		) {
			return extendedDateFieldsFromFilterValue(plantEditFilter.value, nextMode);
		}
		return getDefaultExtendedDateFields(nextMode);
	};

	const resetExtendedPlantForm = nextMode => {
		plantValueForm.resetFields(["value", "valueFrom", "valueTo"]);
		plantValueForm.setFieldsValue(getDefaultExtendedDateFields(nextMode));
	};

	const isSameAttributeFilter = (a, b) =>
		a.key === b.key && a.mode === b.mode && isSameFilterValue(a.value, b.value);

	const getDefaultMode = (_selected = selected) => {
		if (_selected.type === "text") {
			return "full";
		} else if (_selected.type === "number") {
			return "eq";
		} else if (_selected.type === "array") {
			return "full";
		} else if (_selected.type === "date") {
			return "eq";
		} else if (isDateRangeType(_selected.type)) {
			return "between";
		}
		return "";
	};

	const getValuesFields = () => {
		if (selected.type === "date") {
			return [
				{
					label: "Attribute Value",
					name: "value",
					_type: "date",
				},
			];
		}
		if (mode === "in") {
			return [
				{
					label: "Attribute Value",
					name: "value",
					_type: "select-tag",
					options: proposedValues.map(v => ({
						value: v,
						label: String(v),
					})),
				},
			];
		}

		return [{label: "Attribute Value", name: "value", _type: "autocomplete", options: proposedValues}];
	};

	const deleteFilter = attr => {
		const filts = {...props.filters};
		for (const query of filts["or"]) {
			query["and"] = query["and"].filter(x => !isSameAttributeFilter(x, attr));
		}

		const data = {or: []};
		for (const clause of filts["or"]) {
			if (clause["and"].length > 0) {
				data["or"].push(clause);
			}
		}

		if (data["or"].length === 0) {
			data["or"].push({and: []});
		}

		props.onFilterChange(data);
		setEdit(false);
		setFilter(null);
	};

	const addFilter = async (position, idx = 0) => {
		if (compareWith === "attribute" && !comparedField) {
			message.warning("Please select an attribute to compare with.");
			return;
		}
		if (compareWith === "value" && isExtendedDateRangeType(selected.type)) {
			try {
				await validateExtendedDateRangeForm(attrValueForm, mode);
			} catch {
				return;
			}
		} else if (compareWith === "value" && selected.type === "date_range") {
			try {
				await validateDateRangeForm(attrValueForm);
			} catch {
				return;
			}
		}

		let value = compareWith === "attribute" ? getComparisonValue() : attrValueForm.getFieldValue("value");
		const filterMode = selected.type === "date_range" ? "between" : mode;

		if (compareWith === "value" && isExtendedDateRangeType(selected.type)) {
			value = toExtendedDateRangeFilterValue(filterMode, {}, attrValueForm);
		} else if (compareWith === "value" && selected.type === "date_range") {
			value = toDateRangeFilterValue(
				attrValueForm.getFieldValue("valueFrom"),
				attrValueForm.getFieldValue("valueTo"),
			);
		} else if (compareWith === "value" && selected.type === "date") {
			value = toDateFilterValue(value ?? dayjs());
		}

		const filters = props.filters;
		const nextFilter = {
			key: selected.field,
			value,
			mode: filterMode,
			type: selected.type,
			...(compareWith === "attribute" ? {compare_with: "attribute"} : {}),
		};

		if (isEdit) {
			for (const query in filters["or"]) {
				if (filters["or"][query]["and"].find(x => isSameAttributeFilter(x, filter))) {
					filters["or"][query]["and"] = filters["or"][query]["and"].filter(
						x => !isSameAttributeFilter(x, filter),
					);
					filters["or"][query]["and"].push(nextFilter);
					setEdit(false);
					setFilter(null);
				}
			}
			// filters = filters.filter(x => !(x.key === filter.key && x.mode === filter.mode && x.value === filter.value) );
		} else {
			if (position === "and") {
				filters["or"][idx]["and"].push(nextFilter);
			} else if (position === "or") {
				filters["or"].push({
					and: [nextFilter],
				});
			}
		}

		props.onFilterChange({...filters});
	};

	const isSamePlantFilter = (a, b) =>
		a.key === b.key &&
		a.field === b.field &&
		isSameFilterValue(a.value, b.value) &&
		a.mode === b.mode &&
		(a.plant || "") === (b.plant || "");

	const getPlantModesForField = field => {
		if (compareWith === "attribute") {
			return attributeComparisonOptions[field?.type] || [];
		}
		if (field?.type === "number") {
			return numberOptions;
		}
		if (isExtendedDateRangeType(field?.type)) {
			return date_range_extendedOptions;
		}
		return stringOptions;
	};
	const getPlantDefaultMode = field => {
		if (field?.type === "number") {
			return "eq";
		}
		if (isExtendedDateRangeType(field?.type)) {
			return "between";
		}
		return "full";
	};

	const onPlantFieldChange = fieldName => {
		const field = plantInfoFields.find(f => f.field === fieldName) || null;
		setPlantSelectedField(field);
		setPlantMode(getPlantDefaultMode(field));
		setPlantValue(undefined);
		setPlantValueOptions([]);
		setCompareWith("value");
		setComparedField(null);
		if (isExtendedDateRangeType(field?.type)) {
			resetExtendedPlantForm(getPlantDefaultMode(field));
			return;
		}
		plantValueForm.resetFields(["value", "valueFrom", "valueTo"]);
		if (field) {
			setPlantValueLoading(true);
			api.get(`/projects/${props.projectId}/materials/plant-info/values?field=${encodeURIComponent(field.field)}`)
				.then(json => setPlantValueOptions((json.data || []).map(v => ({label: v, value: v}))))
				.finally(() => setPlantValueLoading(false));
		}
	};

	const resetPlantForm = () => {
		setPlantPlant(undefined);
		setPlantSelectedField(null);
		setPlantValue(undefined);
		setPlantValueOptions([]);
		setPlantMode("eq");
		setPlantEditFilter(null);
		setCompareWith("value");
		setComparedField(null);
		plantValueForm.resetFields(["value", "valueFrom", "valueTo"]);
	};

	const resolvePlantFilterValue = async () => {
		if (compareWith === "attribute") {
			return comparedField ? getComparisonValue() : null;
		}
		if (isExtendedDateRangeType(plantSelectedField?.type)) {
			await validateExtendedDateRangeForm(plantValueForm, plantMode);
			return toExtendedDateRangeFilterValue(plantMode, {}, plantValueForm);
		}
		return plantValue;
	};

	const isPlantValueMissing = value => {
		if (Array.isArray(value)) {
			return value.length === 0;
		}
		return value === undefined || value === "" || value === null;
	};

	const buildPlantFilter = value => ({
		key: "plant_info",
		type: "plant_info",
		plant: plantPlant || "",
		field: plantSelectedField.field,
		field_type: plantSelectedField.type,
		value,
		mode: plantMode,
		...(compareWith === "attribute" ? {compare_with: "attribute"} : {}),
	});

	const savePlantEdit = async () => {
		let value;
		try {
			value = await resolvePlantFilterValue();
		} catch {
			return;
		}
		if (!plantSelectedField || isPlantValueMissing(value)) {
			message.warning(compareWith === "attribute"
				? "Please select an attribute to compare with."
				: "Please select a field and a value.");
			return;
		}
		const updated = {...props.filters};
		for (const clause of updated.or) {
			const idx = clause.and.findIndex(f => isSamePlantFilter(f, plantEditFilter));
			if (idx !== -1) {
				clause.and[idx] = buildPlantFilter(value);
				break;
			}
		}
		props.onFilterChange({...updated});
		resetPlantForm();
	};

	const deletePlantEdit = () => {
		const updated = {...props.filters};
		for (const clause of updated.or) {
			clause.and = clause.and.filter(f => !isSamePlantFilter(f, plantEditFilter));
		}
		const cleaned = {or: updated.or.filter(c => c.and.length > 0)};
		if (cleaned.or.length === 0) cleaned.or.push({and: []});
		props.onFilterChange({...cleaned});
		resetPlantForm();
	};

	const addPlantFilter = async (orIdx = 0, asOr = false) => {
		let value;
		try {
			value = await resolvePlantFilterValue();
		} catch {
			return;
		}
		if (!plantSelectedField || isPlantValueMissing(value)) {
			message.warning(compareWith === "attribute"
				? "Please select an attribute to compare with."
				: "Please select a field and a value.");
			return;
		}
		const updated = {...props.filters};
		if (asOr) {
			updated.or.push({and: [buildPlantFilter(value)]});
		} else {
			if (!updated.or[orIdx]) updated.or[orIdx] = {and: []};
			updated.or[orIdx].and.push(buildPlantFilter(value));
		}
		props.onFilterChange({...updated});
		resetPlantForm();
	};

	const getPlantOrDropdownMenu = () => {
		const current = props.filters["or"];
		const options = [];
		if (current.length > 0 && current[0]["and"].length !== 0) {
			options.push(
				<AntdMenu.Item key="or-plant" onClick={() => addPlantFilter(0, true)}>
					Add as an <b>Or</b> clause
				</AntdMenu.Item>,
			);
		}
		for (const idx in current) {
			options.push(
				<AntdMenu.Item key={`and-plant-${idx}`} onClick={() => addPlantFilter(parseInt(idx))}>
					Add to {parseInt(idx) + 1}. <b>And</b> clause
				</AntdMenu.Item>,
			);
		}
		return <AntdMenu>{options}</AntdMenu>;
	};

	const getModeLabel = mode => {
		const all = [
			...numberOptions,
			...stringOptions,
			...date_rangeOptions,
			...date_range_extendedOptions,
			...dateOptions,
		];
		return all.find(o => o.value === mode)?.label || mode;
	};

	const renderPlantInfoFilterTag = (filter) => {
		const fieldLabel = plantInfoFields.find(f => f.field === filter.field)?.label || filter.field;
		const modeLabel = getModeLabel(filter.mode);
		const plantPart = filter.plant ? `Plant: ${filter.plant} - ` : "";
		if (filter.compare_with === "attribute") {
			return `${plantPart}${fieldLabel} ${modeLabel} ${filter.value?.label || filter.value?.key}`;
		}
		const value = isExtendedDateRangeType(filter.field_type)
			? formatExtendedDateRangeValue(filter.value, filter.mode)
			: Array.isArray(filter.value)
				? filter.value.join(", ")
				: filter.value;
		return `[${modeLabel}] ${plantPart}${fieldLabel}: ${value}`;
	};

	const getFilterRepresentation = (criteria = props.filters) => {
		const getTagColor = condition => {
			switch (condition) {
				case "and":
					return "purple";
				case "or":
					return "cyan";
				default:
					return "white";
			}
		};
		if (!criteria || !criteria["or"]) {
			return <div>Scope criteria wasn't saved!</div>;
		} else {
			const blocks = [];
			for (const x of criteria["or"]) {
				if (Array.isArray(x)) {
					blocks.push(
						<Tag style={{padding: 10, display: "block"}}>
							<FilterRepresentation
								break={true}
								filters={x}
								labels={{...attributeLabels, ...labels}}
								deleteFilter={editFilter}
							/>
						</Tag>,
					);
				} else {
					for (const cond in x) {
						const regularFilters = x[cond].filter(f => f.type !== "plant_info");
						const plantInfoFilters = x[cond].filter(f => f.type === "plant_info");
						const title = x[cond].length > 1 ? <div>And</div> : <div />;

						if (x[cond] && x[cond].length > 0) {
							blocks.push(
								<Row style={{padding: 5}}>
									<Tag icon={title} color={getTagColor(cond)} style={{padding: 10}}>
										{regularFilters.length > 0 && (
											<FilterRepresentation
												breakLines={true}
												filters={regularFilters}
												labels={{...attributeLabels, ...labels}}
												deleteFilter={editFilter}
											/>
										)}
										{plantInfoFilters.map((f, i) => (
											<div key={i} style={{marginTop: regularFilters.length > 0 || i > 0 ? 4 : 0}}>
												<Tag
													color={plantEditFilter && isSamePlantFilter(f, plantEditFilter) ? "blue" : "geekblue"}
													style={{cursor: "pointer"}}
													onClick={() => {
												setPlantEditFilter(f);
												setLeftPanel("plant_info");
											}}>
													{renderPlantInfoFilterTag(f)}
												</Tag>
											</div>
										))}
									</Tag>
								</Row>,
							);
						}
					}
				}
			}

			return (
				<Tag style={{padding: 10}} color={"magenta"} icon={<div>Or</div>}>
					{blocks}
				</Tag>
			);
		}
	};

	const editFilter = filter => {
		const attr = fields.find(x => x.field === filter.key);
		setFilter(filter);
		if (attr) {
			setEdit(true);
			setSelected(attr);
			setMode(filter.mode);
			if (filter.compare_with === "attribute") {
				setCompareWith("attribute");
				setComparedField(comparableFields.find(f => f.key === filter.value?.key) || null);
			} else if (isExtendedDateRangeType(filter.type)) {
				attrValueForm.setFieldsValue(extendedDateFieldsFromFilterValue(filter.value, filter.mode));
			} else if (filter.type === "date_range") {
				attrValueForm.setFieldsValue(dateRangeFieldsFromFilterValue(filter.value));
			} else {
				const formValue = attr.type === "date" ? dayjs(filter.value) : filter.value;
				attrValueForm.setFieldValue("value", formValue);
            }
			setLeftPanel("attributes");
		}
	};

	const getSuffix = idx => {
		if (idx === 1) {
			return "st";
		} else if (idx === 2) {
			return "nd";
		} else if (idx === 3) {
			return "rd";
		}
		return "th";
	};

	const getDropdownMenu = () => {
		const current = props.filters["or"];

		const options = [];

		if (current.length > 0 && current[0]["and"].length !== 0) {
			options.push(
				<AntdMenu.Item key={"or"} onClick={() => addFilter("or")}>
					Add as an <b>Or</b> clause
				</AntdMenu.Item>,
			);
		}

		for (const clause in current) {
			options.push(
				<AntdMenu.Item key={`and-${clause}`} onClick={() => addFilter("and", clause)}>
					Add to {parseInt(clause) + 1}
					{getSuffix(parseInt(clause) + 1)} <b>And</b> clause
				</AntdMenu.Item>,
			);
		}

		return <AntdMenu>{options}</AntdMenu>;
	};

	const isFilterEmpty = () => {
		const orClause = props.filters?.or;
		return !orClause?.length || (orClause.length === 1 && !orClause[0]?.and?.length);
	};

	const submitAttributeForm = () => {
		if (isEdit) {
			return addFilter();
		}
		if (isFilterEmpty()) {
			return addFilter("and", 0);
		}
		return undefined;
	};

	const submitPlantForm = () => {
		if (isPlantEdit) {
			return savePlantEdit();
		}
		if (isFilterEmpty()) {
			return addPlantFilter(0);
		}
		return undefined;
	};

	return (
		<div style={{margin: 10}}>
			<Spin spinning={loading}>
				<Descriptions size={"small"} style={{width: "100%", margin: 5}} bordered column={1}>
					<Descriptions.Item labelStyle={{width: "20%"}} label={"Items in current selection"}>
						<Spin spinning={props.countLoading}>
							<Statistic valueStyle={{fontSize: 16}} value={props.total} />
						</Spin>
					</Descriptions.Item>
				</Descriptions>
				<Row style={{marginTop: 5}}>
					<Col span={7} style={{margin: 5}}>
						<Radio.Group
							value={leftPanel}
							onChange={e => {
								setLeftPanel(e.target.value);
								if (e.target.value === "plant_info") {
									setSelected({});
									setEdit(false);
									setFilter(null);
									setCompareWith("value");
									setComparedField(null);
									attrValueForm.resetFields(["value"]);
								} else {
									setPlantEditFilter(null);
									setCompareWith("value");
									setComparedField(null);
								}
							}}
							style={{width: "100%", marginBottom: 8, display: "flex"}}>
							<Radio.Button value="attributes" style={{flex: 1, textAlign: "center"}}>
								Attributes
							</Radio.Button>
							<Radio.Button value="plant_info" style={{flex: 1, textAlign: "center"}}>
								Plant Info
							</Radio.Button>
						</Radio.Group>

						{leftPanel === "attributes" ? (
							<DataCard
								style={{
									maxHeight: "55vh",
									overflow: "scroll",
								}}>
								<List
									size="small"
									bordered
									style={{padding: 0, margin: -10}}
									dataSource={fields}
									renderItem={item => (
										<List.Item
											className={
												selected.label === item.label ? "ant-dropdown-menu-item-selected" : ""
											}
											onClick={() => {
												setSelected(item);
												setFilter(null);
												setEdit(false);
												setCompareWith("value");
												setComparedField(null);
												if (item.type === "date_range") {
													attrValueForm.setFieldsValue(getDefaultDateRangeFields());
													setMode("between");
												} else if (isExtendedDateRangeType(item.type)) {
													resetExtendedAttributeForm("between");
													setMode("between");
												} else {
													attrValueForm.resetFields(["value", "valueFrom", "valueTo"]);
												}
											}}>
											{item.label}
										</List.Item>
									)}
								/>
							</DataCard>
						) : (
							<PlantInfoFilter
								isEdit={isPlantEdit}
								plant={plantPlant}
								setPlant={setPlantPlant}
								plantOptions={plantPlantOptions}
								plantLoading={plantPlantLoading}
								selectedField={plantSelectedField}
								onFieldChange={onPlantFieldChange}
								plantInfoFields={plantInfoFields}
							/>
						)}
					</Col>
				<Col span={8} style={{margin: 5}}>
					{leftPanel === "attributes" && selected.label && (
						<DataCard
							title={
								<div>
									Attribute: <b>{selected.label}</b>
								</div>
							}>
							<Form layout="vertical">
								<Form.Item label="Compare with" style={{marginBottom: 8}}>
									<Radio.Group
										value={compareWith}
										onChange={e => {
											const nextCompareWith = e.target.value;
											setCompareWith(nextCompareWith);
											setComparedField(null);
											setMode(nextCompareWith === "attribute" ? "eq" : getDefaultMode());
										}}>
										<Radio value="value">Value</Radio>
										<Radio value="attribute" disabled={!attributeComparisonOptions[selected.type]}>
											Attribute
										</Radio>
									</Radio.Group>
								</Form.Item>
								{compareWith === "attribute" && (
									<Form.Item label="Attribute" required style={{marginBottom: 8}}>
										<Select
											showSearch
											placeholder="Select attribute..."
											value={comparedField?.id}
											onChange={onComparedFieldChange}
											options={getComparisonFieldOptions(selected.type, selected.field)}
											optionFilterProp="label"
										/>
									</Form.Item>
								)}
							</Form>

							{compareWith === "value" && selected && selected.field && selected.type === "date_range" && (
								<DateRangeFilterForm
									form={attrValueForm}
									initialValues={
										isEdit && filter?.type === "date_range"
											? dateRangeFieldsFromFilterValue(filter.value)
											: getDefaultDateRangeFields()
									}
									onFinish={submitAttributeForm}
								/>
							)}

							{compareWith === "value" && selected && selected.field && isExtendedDateRangeType(selected.type) && (
								<ExtendedDateRangeFilterForm
									key={`${selected.field}-${mode}`}
									form={attrValueForm}
									mode={mode}
									initialValues={getExtendedAttributeInitialValues(mode)}
									onFinish={submitAttributeForm}
								/>
							)}

							{compareWith === "value" && selected && selected.field && !isDateRangeType(selected.type) && (
								<StandardForm
									form={attrValueForm}
									fields={getValuesFields()}
									onFinish={submitAttributeForm}
								/>
							)}
							{selected && selected.field && getOptionsByType().length > 0 && (
								<Space>
									<Radio.Group
										style={{margin: 10}}
										onChange={e => {
											const nextMode = e.target.value;
											setMode(nextMode);
											if (isExtendedDateRangeType(selected.type)) {
												resetExtendedAttributeForm(nextMode);
											}
										}}
										value={mode}>
										<Space direction={"vertical"}>
											{getOptionsByType().map(item => (
												<Radio key={item.value} value={item.value}>
													{item.label}
												</Radio>
											))}
										</Space>
									</Radio.Group>
								</Space>
							)}
							<Row>
								<Col span={8}>
									{isEdit && (
										<Button type={"danger"} onClick={() => deleteFilter(filter)}>
											Delete
										</Button>
									)}
								</Col>
								<Col span={16}>
									{isEdit ? (
										<Button onClick={addFilter} type={"primary"}>
											Save
										</Button>
									) : isFilterEmpty() ? (
										<Button
											className="float-right"
											type={"primary"}
											onClick={() => addFilter("and", 0)}>
											Add
										</Button>
									) : (
										<Dropdown className="float-right" overlay={getDropdownMenu()}>
											<Button type={"primary"}>Add</Button>
										</Dropdown>
									)}
								</Col>
							</Row>
						</DataCard>
					)}
					{leftPanel === "plant_info" && plantSelectedField && (
						<DataCard
							title={
								<div>
									Plant Info: <b>{plantSelectedField.label}</b>
								</div>
							}
							style={{borderColor: isPlantEdit ? "#1890ff" : undefined}}>
							<Form layout="vertical">
								<Form.Item label="Compare with" style={{marginBottom: 8}}>
									<Radio.Group
										value={compareWith}
										onChange={e => {
											const nextCompareWith = e.target.value;
											setCompareWith(nextCompareWith);
											setComparedField(null);
											setPlantMode(
												nextCompareWith === "attribute"
													? "eq"
													: getPlantDefaultMode(plantSelectedField),
											);
										}}>
										<Radio value="value">Value</Radio>
										<Radio
											value="attribute"
											disabled={!attributeComparisonOptions[plantSelectedField.type]}>
											Attribute
										</Radio>
									</Radio.Group>
								</Form.Item>
								{compareWith === "attribute" && (
									<Form.Item label="Attribute" required style={{marginBottom: 8}}>
										<Select
											showSearch
											placeholder="Select attribute..."
											value={comparedField?.id}
											onChange={onComparedFieldChange}
											options={getComparisonFieldOptions(
												plantSelectedField.type,
												`plant_info.${plantSelectedField.field}`,
											)}
											optionFilterProp="label"
										/>
									</Form.Item>
								)}
							</Form>

							{compareWith === "value" && isExtendedDateRangeType(plantSelectedField.type) ? (
								<ExtendedDateRangeFilterForm
									key={`${plantSelectedField.field}-${plantMode}`}
									form={plantValueForm}
									mode={plantMode}
									initialValues={
										getExtendedPlantInitialValues(plantMode)
									}
									onFinish={submitPlantForm}
								/>
							) : compareWith === "value" ? (
								<Form layout="horizontal">
									<Form.Item label={<b>Value</b>} style={{marginBottom: 8}}>
										{plantMode === "in" ? (
											<Select
												style={{width: "100%"}}
												mode="tags"
												placeholder={`Select ${plantSelectedField.label.toLowerCase()}...`}
												loading={plantValueLoading}
												value={plantValue}
												onChange={val => setPlantValue(val)}
												options={plantValueOptions}
												allowClear
											/>
										) : (
											<AutoComplete
												style={{width: "100%"}}
												placeholder={`Type or select ${plantSelectedField.label.toLowerCase()}...`}
												loading={plantValueLoading}
												value={plantValue}
												onChange={val => setPlantValue(val)}
												options={plantValueOptions.filter(o =>
													!plantValue ||
													String(o.value).toLowerCase().includes(String(plantValue).toLowerCase())
												)}
												filterOption={false}
											/>
										)}
									</Form.Item>
								</Form>
							) : null}
							<Space>
								<Radio.Group
									style={{margin: 10}}
									onChange={e => {
										const nextMode = e.target.value;
										setPlantMode(nextMode);
										setPlantValue(nextMode === "in" ? [] : undefined);
										if (isExtendedDateRangeType(plantSelectedField.type)) {
											resetExtendedPlantForm(nextMode);
										}
									}}
									value={plantMode}>
									<Space direction={"vertical"}>
										{getPlantModesForField(plantSelectedField).map(item => (
											<Radio key={item.value} value={item.value}>
												{item.label}
											</Radio>
										))}
									</Space>
								</Radio.Group>
							</Space>
							<Row>
								<Col span={8}>
									{isPlantEdit && (
										<Button type={"danger"} onClick={deletePlantEdit}>
											Delete
										</Button>
									)}
								</Col>
								<Col span={16}>
									{isPlantEdit ? (
										<Button className="float-right" type={"primary"} onClick={savePlantEdit}>
											Save
										</Button>
									) : isFilterEmpty() ? (
										<Button
											className="float-right"
											type={"primary"}
											onClick={() => addPlantFilter(0)}>
											Add
										</Button>
									) : (
										<Dropdown className="float-right" overlay={getPlantOrDropdownMenu()}>
											<Button type={"primary"}>Add</Button>
										</Dropdown>
									)}
								</Col>
							</Row>
						</DataCard>
					)}
				</Col>
					<Col span={8} style={{margin: 5}}>
						<DataCard title="Filters">{getFilterRepresentation()}</DataCard>
					</Col>
				</Row>
			</Spin>
		</div>
	);
}

function ScopeMode(props) {
	const [mode, setMode] = React.useState("new");
	const [loading, setLoading] = React.useState(false);
	const [scopes, setScopes] = React.useState([]);

	const copy = () => {
		if (scopes.length === 0) {
			setLoading(true);
			api.post(`/projects/${props.projectId}/scopes?archived=${props.archived}`, {_filters: {}, _query: ""})
				.then(json => {
					setScopes(json.data.items);
					props.onScopeSelect(0, json.data.items[0]);
				})
				.finally(() => setLoading(false));
		}
	};

	const options = [
		{
			label: "Create filters from scratch",
			value: "new",
			onClick: () => {
				props.resetChoice();
				props.openNext("new");
			},
		},
		{
			label: "Copy filters from another scope",
			value: "copy",
			onClick: () => copy(),
		},
	];

	return (
		<DataCard style={{margin: 10}} title={"Select what do you want to do: "}>
			<Spin spinning={loading}>
				<Radio.Group style={{margin: 10}} onChange={e => setMode(e.target.value)} value={mode}>
					<Space direction="vertical">
						{options.map((item, i) => (
							<Radio onClick={item.onClick} value={item.value}>
								{item.label}
							</Radio>
						))}
					</Space>
				</Radio.Group>
				<div>
					{mode === "copy" && (
						<Select
							style={{width: 500}}
							value={props.selected}
							onChange={idx => props.onScopeSelect(idx, scopes[idx])}
							options={scopes.map((item, idx) => ({
								label: `${item.name} (${item.quantity || 0} items)`,
								value: idx,
							}))}
						/>
					)}
				</div>
			</Spin>
		</DataCard>
	);
}

function AddToScope(props) {
	const submittingRef = React.useRef(false);
	const [mode, setMode] = React.useState("new");
	const [loading, setLoading] = React.useState(false);

	const [scopes, setScopes] = React.useState([]);
	const [selected, setSelected] = React.useState();

	const copy = () => {
		if (scopes.length === 0) {
			setLoading(true);
			api.post(`/projects/${props.projectId}/scopes?archived=${props.archived}`, {_filters: {}, _query: ""})
				.then(json => {
					setScopes(json.data.items);
					setSelected(0);
				})
				.finally(() => setLoading(false));
		}
	};

	const options = () => {
		const _options = [
			{
				label: props.isUpdate ? "Update scope" : "Create new scope",
				value: "new",
				onClick: () => null,
			},
		];
		if (!props.isUpdate) {
			_options.push({
				label: "Update existing scope",
				value: "add",
				onClick: () => copy(),
			});
		}

		return _options;
	};

	const [form] = useForm();

	useEffect(() => {
		props.setForm(form);
	}, [form]);

	useEffect(() => {
		if (props.scope && props.isUpdate) {
			form.setFieldValue("name", props.scope.name);
			form.setFieldValue("description", props.scope.description);
		}
	}, [props.scope]);

	const lockDoneUntilModalReopen = props.lockDoneUntilModalReopen !== false;

	const addToExisting = scope => {
		if (submittingRef.current) return;

		const hide = message.loading("Adding items to scope...", 0, () => {});
		const finishAlways = () => {
			submittingRef.current = false;
			setTimeout(hide, 10);
		};

		if (mode === "new") {
			submittingRef.current = true;
			if (lockDoneUntilModalReopen) {
				props.onDoneSubmitStart?.();
			}
			if (!Object.keys(scope).includes("type")) {
				scope["type"] = "user-defined";
			}

			const endpoint = props.isUpdate ? "edit" : "add";

			scope["filters"] = props.filters;
			if (props.isUpdate) {
				scope["_id"] = props.scope._id;
			}
			api.post(`/projects/${props.projectId}/scopes/${endpoint}?archived=${props.archived}`, scope, {omitGlobalError: true})
				.then(() => {
					message.success(`The scope ${scope.name} was successfully ${props.isUpdate ? "updated" : "created"}`);
					props.onFinish();
				})
				.catch(error => {
                    message.error(
                        error.response?.status === 409
                            ? "Scope with this name already exists, please change name to continue"
                            : "Something went wrong during saving! Please try again later.",
                    );
					if (lockDoneUntilModalReopen) {
						props.onDoneSubmitError?.();
					}
				})
				.finally(finishAlways);
		} else if (mode === "add") {
			submittingRef.current = true;
			if (lockDoneUntilModalReopen) {
				props.onDoneSubmitStart?.();
			}
			api.post(
				`/projects/${props.projectId}/scopes/add/items?id=${scope._id}&archived=${props.archived}`,
				props.filters,
			)
				.then((res) => {
					const targetScope = scopes.find(s => s._id === scope._id);
					const scopeName = targetScope ? targetScope.name : "";
					message.success(`The scope "${scopeName}" has been updated. The new material count is ${res.data.quantity}.`);
					props.onFinish();
				})
				.catch(() => {
					if (lockDoneUntilModalReopen) {
						props.onDoneSubmitError?.();
					}
				})
				.finally(finishAlways);
		} else {
			setTimeout(hide, 10);
		}
	};

	const fields = [
		{label: "Scope Name", name: "name", _type: "string", message: "Error", required: true},
		{label: "Scope Description", name: "description", _type: "long_text", message: "Error", required: false},
	];

	const getFieldsByType = () => {
		if (mode === "new") {
			return fields;
		} else {
			return [
				{
					label: "Scope Name",
					name: "_id",
					_type: "select-object",
					message: "Error",
					required: true,
					options: scopes.map((item, i) => ({
						label: `${item.name} (${item.quantity || 0} items)`,
						value: item._id,
					})),
				},
			];
		}
	};

	return (
		<div style={{margin: 10}}>
			<Descriptions size={"small"} style={{width: "100%", margin: 5}} bordered column={2}>
				<Descriptions.Item labelStyle={{width: "20%"}} label={"Items in current selection"}>
					<Spin spinning={props.countLoading}>
						<Statistic valueStyle={{fontSize: 16}} value={props.total} />
					</Spin>
				</Descriptions.Item>
			</Descriptions>

			<Radio.Group style={{width: 700, margin: 5}} onChange={e => setMode(e.target.value)} value={mode}>
				<Space direction="vertical">
					{options().map((item, i) => (
						<Radio onClick={item.onClick} value={item.value}>
							{item.label}
						</Radio>
					))}
					<div>
						{mode === "add" && (
							<DataCard title={"Update criteria in existing scope"}>
								<StandardForm
									otherProps={{style: {width: 700}}}
									form={form}
									initialValues={props.scope}
									onFinish={addToExisting}
									fields={getFieldsByType()}
								/>
							</DataCard>
						)}

						{mode === "new" && (
							<DataCard title={props.isUpdate ? "Edit scope" : "Create new scope"}>
								<StandardForm
									otherProps={{style: {width: 700}}}
									form={form}
									initialValues={{}}
									onFinish={addToExisting}
									fields={getFieldsByType()}
								/>
							</DataCard>
						)}
					</div>
				</Space>
			</Radio.Group>
		</div>
	);
}

export class CreateScope extends Component {
	constructor(props) {
		super(props);
		this.state = {
			currentStep: 0,
			fields: [],
			filters: {
				or: [
					{
						and: [],
					},
				],
			},
			total: 0,
			countLoading: false,
			form: null,
			selectedScopeIdx: 0,
			selectedScope: {criteria: {}},
			doneSubmitLocked: false,
		};
		this._countRequestId = 0;
	}

	fetchMaterialCount = () => {
		const {projectId} = this.props;
		if (!projectId) {
			return;
		}

		this._countRequestId += 1;
		const requestId = this._countRequestId;
		this.setState({countLoading: true});

		api.post(`/projects/${projectId}/materials/count`, {filters: this.state.filters})
			.then(json => {
				if (requestId === this._countRequestId) {
					this.setState({total: json.data.total});
				}
			})
			.finally(() => {
				if (requestId === this._countRequestId) {
					this.setState({countLoading: false});
				}
			});
	};

	componentDidMount() {
		this.fetchMaterialCount();
	}

	componentDidUpdate(prevProps, prevState, snapshot) {
		if (prevState.filters !== this.state.filters || prevProps.projectId !== this.props.projectId) {
			this.fetchMaterialCount();
		}

		if (prevProps.isModalVisible !== this.props.isModalVisible) {
			let currentStep = 0;
			let scope = {criteria: {}};
			let isUpdate = false;
			if (!this.props.isModalVisible) {
				this.setState({
					currentStep: currentStep,
					fields: [],
					isUpdate: isUpdate,
					filters: {
						or: [
							{
								and: [],
							},
						],
					},
					total: 0,
					countLoading: false,
					form: null,
					selectedScopeIdx: 0,
					selectedScope: scope,
					doneSubmitLocked: false,
				});
			} else {
				if (this.props.scope) {
					currentStep = 1;
					scope = this.props.scope;
					isUpdate = true;
					this.setState({
						currentStep: currentStep,
						filters: scope.criteria,
						isUpdate: isUpdate,
						selectedScope: scope,
						doneSubmitLocked: false,
					});
				} else {
					this.setState({doneSubmitLocked: false});
				}
			}
		}
	}

	createScope() {
		const lock =
			this.props.lockDoneUntilModalReopen !== false && this.state.doneSubmitLocked;
		if (lock) {
			return;
		}
		this.state.form.submit();
	}

	openNext = action => {
		if (action === "new") {
			this.goToNextStep();
		}
	};

	resetChoice = () => {
		this.setState({
			currentStep: 0,
			fields: [],
			filters: {
				or: [
					{
						and: [],
					},
				],
			},
			total: 0,
			form: null,
			selectedScopeIdx: 0,
			selectedScope: {criteria: {}},
		});
	};

	getSteps = () => [
		{
			title: "Select mode",
			key: "mode",
			content: (
				<ScopeMode
					openNext={this.openNext}
					projectId={this.props.projectId}
					selected={this.state.selectedScopeIdx}
					archived={this.props.archived}
					resetChoice={this.resetChoice}
					onScopeSelect={(idx, scope) => {
						this.setState({
							selectedScopeIdx: idx,
							selectedScope: scope,
							filters: scope.criteria,
						});
					}}
				/>
			),
		},
		{
			title: "Select Filters",
			key: 0,
			content: (
				<MaterialFilter
					total={this.state.total}
					countLoading={this.state.countLoading}
					projectId={this.props.projectId}
					criteria={this.state.selectedScope.criteria}
					filters={this.state.filters}
					onFilterChange={filters => {
						this.setState({filters});
					}}
				/>
			),
		},
		{
			title: "Attach to a Scope",
			key: 1,
			content: (
				<AddToScope
					scope={this.state.selectedScope}
					isUpdate={this.state.isUpdate}
					projectId={this.props.projectId}
					onFinish={() => this.props.setIsModalVisible(false)}
					setForm={form => this.setState({form: form})}
					filters={this.state.filters}
					total={this.state.total}
					countLoading={this.state.countLoading}
					archived={this.props.archived}
					lockDoneUntilModalReopen={this.props.lockDoneUntilModalReopen}
					onDoneSubmitStart={() => this.setState({doneSubmitLocked: true})}
					onDoneSubmitError={() => this.setState({doneSubmitLocked: false})}
				/>
			),
		},
	];

	goToNextStep = () => {
		this.setState({currentStep: this.state.currentStep + 1});
	};

	goToPreviousStep = () => {
		this.setState({currentStep: this.state.currentStep - 1});
		if (this.state.currentStep === 1) {
			this.resetChoice();
		}
	};

	closeModal = () => {
		this.props.setIsModalVisible(false);
	};

	render() {
		const doneLocked =
			this.props.lockDoneUntilModalReopen !== false && this.state.doneSubmitLocked;
		return (
			<ModalWindow
				modalProps={{width: "80vw", height: "80vh", maxHeight: "80vh", overflowY: "auto", footer: null}}
				title={this.state.isUpdate ? "Edit scope" : "Create new scope"}
				isModalVisible={this.props.isModalVisible}
				onOk={this.closeModal}
				onCancel={this.closeModal}>
				<div
					style={{
						display: "flex",
						flexDirection: "column",
						height: "calc(80vh - 48px)",
					}}>
					<Steps current={this.state.currentStep} items={this.getSteps()} style={{flexShrink: 0}} />
					<div
						style={{
							flex: 1,
							minHeight: 0,
							overflowY: "auto",
							margin: "8px 0",
						}}>
						{this.getSteps()[this.state.currentStep].content}
					</div>
					<div
						className="steps-action"
						style={{
							flexShrink: 0,
							overflow: "hidden",
							paddingTop: 8,
							borderTop: "1px solid #f0f0f0",
						}}>
						{(this.state.isUpdate ? this.state.currentStep > 1 : this.state.currentStep > 0) && (
							<Button
								style={{
									margin: "0 8px",
								}}
								onClick={() => this.goToPreviousStep()}>
								Previous
							</Button>
						)}
						{this.state.currentStep < this.getSteps().length - 1 && (
							<Button
								className="float-right"
								type="primary"
								onClick={() => this.goToNextStep()}
								style={{
									margin: "0 8px",
								}}>
								Next
							</Button>
						)}
						{this.state.currentStep === this.getSteps().length - 1 && (
							<Button
								type="primary"
								className="float-right"
								loading={doneLocked}
								disabled={doneLocked}
								onClick={() => this.createScope()}
								style={{
									margin: "0 8px",
								}}>
								Done
							</Button>
						)}
					</div>
				</div>
			</ModalWindow>
		);
	}
}
